import { useEffect, useMemo, useState } from 'react';
import { onSnapshot } from 'firebase/firestore';
import {
  buildLeaveRequestsQuery,
  mapLeaveRequestSnapshot,
} from '../services/leaveService';
import {
  LeaveRequestFilter,
  LeaveRequestRecord,
  UseLeaveRequestsResult,
} from '../types';

const normaliseFilter = (filter: LeaveRequestFilter | null | undefined): LeaveRequestFilter | null => {
  if (!filter) return null;

  const managerLocations = filter.managerLocations
    ? filter.managerLocations
        .map((location) => location?.trim())
        .filter((location): location is string => Boolean(location && location.length > 0))
    : undefined;

  return {
    ...filter,
    managerLocations: managerLocations && managerLocations.length > 0 ? managerLocations : undefined,
  };
};

const serialiseFilter = (filter: LeaveRequestFilter | null): string => {
  if (!filter) return 'null';
  const { managerLocations, ...rest } = filter;
  const sortedLocations = managerLocations ? [...managerLocations].sort() : undefined;
  return JSON.stringify({ ...rest, managerLocations: sortedLocations });
};

export const useLeaveRequests = (filter: LeaveRequestFilter | null): UseLeaveRequestsResult => {
  const [data, setData] = useState<LeaveRequestRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const normalisedFilter = useMemo(() => normaliseFilter(filter), [filter]);
  const filterKey = useMemo(() => serialiseFilter(normalisedFilter), [normalisedFilter]);

  useEffect(() => {
    const activeFilter = normalisedFilter;

    if (!activeFilter) {
      setData([]);
      setLoading(false);
      setError(null);
      return undefined;
    }

    let unsubscribe = () => {};

    try {
      const builtQuery = buildLeaveRequestsQuery(activeFilter);
      setLoading(true);
      setError(null);

      unsubscribe = onSnapshot(
        builtQuery,
        (snapshot) => {
          const mapped = snapshot.docs.map(mapLeaveRequestSnapshot);
          setData(mapped);
          setLoading(false);
        },
        (snapshotError) => {
          console.error('Error subscribing to leave requests:', snapshotError);
          setError(snapshotError as Error);
          setLoading(false);
        }
      );
    } catch (queryError) {
      setError(queryError as Error);
      setData([]);
      setLoading(false);
      return undefined;
    }

    return () => {
      unsubscribe();
    };
  }, [filterKey]);

  return { data, loading, error };
};
